import React, { useCallback } from 'react';
import UDFilterChip from 'ud-ui/components/filter-chip';
import { UDStatusTone } from 'ud-ui/components/status-tone';

/**
 * Цвет чипа по исходу заявки. «neutral» — чип «Все»: он не про исход, поэтому
 * выбранным красится тёмной заливкой, а не жёлтым: жёлтый — это «ожидает».
 */
export type ApplicationStatusChipTone = 'neutral' | 'pending' | 'accepted' | 'declined';

export interface ApplicationStatusChipOption<T extends string> {
  value: T;
  label: string;
  count: number;
  tone?: ApplicationStatusChipTone;
}

interface ApplicationStatusChipsProps<T extends string> {
  options: ApplicationStatusChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/** Исход заявки → тон кита. Тот же тон у бейджа статуса, так что фильтр и статус одного цвета. */
const KIT_TONE: Record<ApplicationStatusChipTone, UDStatusTone> = {
  neutral: 'neutral',
  pending: 'pending',
  accepted: 'success',
  declined: 'error',
};

/**
 * Чипы статусов заявок: «Все / Ожидают решения / Заявлены / Отклонены» — фильтр с выбором
 * одного. Вид и состояния — китовый `UDFilterChip`; здесь только набор и выбор.
 */
function ApplicationStatusChips<T extends string>({ options, value, onChange, className }: ApplicationStatusChipsProps<T>) {
  // Один стабильный обработчик на все чипы: значение — в `data-value`, иначе новая стрелка
  // на каждый рендер обнуляла бы `React.memo` у `UDFilterChip`.
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => onChange(event.currentTarget.dataset.value as T),
    [onChange],
  );

  return (
    <div className={className} role="group">
      <div className="flex flex-wrap items-center gap-2">
        {options.map((option) => (
          <UDFilterChip
            key={option.value}
            tone={KIT_TONE[option.tone ?? 'neutral']}
            selected={option.value === value}
            count={option.count}
            data-value={option.value}
            onClick={handleClick}
          >
            {option.label}
          </UDFilterChip>
        ))}
      </div>
    </div>
  );
}

export default ApplicationStatusChips;
